import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { Search, ChevronDown, MoreHorizontal } from 'lucide-react'
import { useAppSelector } from '@/store/hooks'
import Button from '@/components/ui/Button'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import MemberSelect from '@/components/ui/MemberSelect'
import { designTokens } from '@/utils/design-tokens'
import PerformanceHierarchyTable from './components/PerformanceHierarchyTable'
import PerformanceDetailPanel, { type PerformanceNodeType } from './components/PerformanceDetailPanel'
import PerformanceIndicatorCreatePanel from './components/PerformanceIndicatorCreatePanel'
import PerformanceGroupEditPanel from './components/PerformanceGroupEditPanel'
import PerformanceCategoryEditPanel from './components/PerformanceCategoryEditPanel'
import CategoryCreatePanel from './components/CategoryCreatePanel'
import PerformanceClassCreatePanel from './components/PerformanceClassCreatePanel'
import PerformanceClassEditPanel from './components/PerformanceClassEditPanel'
import PerformanceAddSelectionModal from './components/PerformanceAddSelectionModal'
import PerformanceCreatePanel from './components/PerformanceCreatePanel'
import PerformanceEditPanel from './components/PerformanceEditPanel'
import TaskSelectModal from './components/TaskSelectModal'
import PerformanceBulkCreateModal from './components/PerformanceBulkCreateModal'
import TaskCreatePanel from '../TaskManagement/components/TaskCreatePanel'
import { PerformanceTreeNode, PerformanceHierarchyTableRef } from './types'
import { PerformanceService } from '@/api/generated/services/PerformanceService'
import { PerformanceGroupService } from '@/api/generated/services/PerformanceGroupService'
import { PerformanceCategoryService } from '@/api/generated/services/PerformanceCategoryService'
import { PerformanceMapService } from '@/api/generated/services/PerformanceMapService'
import { BusinessService } from '@/api/generated/services/BusinessService'
import { TaskService } from '@/api/generated/services/TaskService'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import type { PerformanceMapCreateRequest } from '@/api/generated/models/PerformanceMapCreateRequest'
import { transformPerformanceData } from './utils/performanceUtils'
import { usePersistedBusinessFilter } from '@/hooks/usePersistedBusinessFilter'

const PerformanceManagementPage: React.FC = () => {
  const currentWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const orgMember = useAppSelector(state => state.auth.orgMember)
  const hasAdminPrivileges = orgMember?.role === 'owner' || orgMember?.role === 'admin'
  const { showError } = useAlert()

  const tableRef = useRef<PerformanceHierarchyTableRef>(null)
  
  // API state
  const [performanceData, setPerformanceData] = useState<PerformanceTreeNode[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Business list state
  const [businesses, setBusinesses] = useState<Array<{id: string, name: string}>>([])
  const [isBusinessesLoading, setIsBusinessesLoading] = useState(false)
  const [businessesError, setBusinessesError] = useState<string | null>(null)
  
  // UI state for title area controls
  const {
    businessId: selectedBusinessId,
    businessName: selectedBusiness,
    setBusiness: saveSelectedBusiness,
  } = usePersistedBusinessFilter()
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('')
  const [selectedDepartmentName, setSelectedDepartmentName] = useState('')
  const [selectedMemberId, setSelectedMemberId] = useState('')
  const [selectedMemberName, setSelectedMemberName] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false)
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false)

  // Detail panel state — id/type만 보관, 데이터는 PerformanceDetailPanel이 자체 fetch.
  // refreshKey를 올리면 패널이 재조회.
  const [selectedPerformanceId, setSelectedPerformanceId] = useState<string | null>(null)
  const [selectedPerformanceType, setSelectedPerformanceType] = useState<PerformanceNodeType | null>(null)
  const [detailRefreshKey, setDetailRefreshKey] = useState(0)
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)
  
  // Performance indicator create panel state
  const [isIndicatorCreatePanelOpen, setIsIndicatorCreatePanelOpen] = useState(false)
  const [selectedBusinessForIndicator, setSelectedBusinessForIndicator] = useState<{id: string, name: string} | null>(null)

  // Performance group edit panel state
  const [isGroupEditPanelOpen, setIsGroupEditPanelOpen] = useState(false)
  const [selectedGroupForEdit, setSelectedGroupForEdit] = useState<PerformanceTreeNode | null>(null)

  // Performance category edit panel state
  const [isCategoryEditPanelOpen, setIsCategoryEditPanelOpen] = useState(false)
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState<PerformanceTreeNode | null>(null)

  // Category create panel state
  const [isCategoryCreatePanelOpen, setIsCategoryCreatePanelOpen] = useState(false)
  const [categoryCreateData, setCategoryCreateData] = useState<{
    parentId: string
    parentName: string
    groupId: string
    groupName: string
    categoryType: '분류'
    isDirectUnderGroup: boolean
  } | null>(null)

  // Performance class create panel state
  const [isPerformanceClassCreatePanelOpen, setIsPerformanceClassCreatePanelOpen] = useState(false)
  const [performanceClassCreateData, setPerformanceClassCreateData] = useState<{
    categoryId: string
    categoryName: string
  } | null>(null)

  // Performance class edit panel state
  const [isPerformanceClassEditPanelOpen, setIsPerformanceClassEditPanelOpen] = useState(false)
  const [selectedPerformanceClassForEdit, setSelectedPerformanceClassForEdit] = useState<PerformanceTreeNode | null>(null)

  // Performance edit panel state
  const [isPerformanceEditPanelOpen, setIsPerformanceEditPanelOpen] = useState(false)
  const [selectedPerformanceForEdit, setSelectedPerformanceForEdit] = useState<PerformanceTreeNode | null>(null)

  // Task edit panel state (for linked performances)
  const [isTaskEditPanelOpen, setIsTaskEditPanelOpen] = useState(false)
  const [isLoadingTaskData, setIsLoadingTaskData] = useState(false)
  const [taskEditData, setTaskEditData] = useState<{
    taskId: string
    taskSubgroupId: string
    taskSubgroupName: string
    taskGroupName: string
    businessName: string
    businessId: string
    initialTaskData?: {
      name?: string
      assigneeId?: string
      assigneeName?: string
      subAssigneeId?: string
      subAssigneeName?: string
      managerId?: string
      managerName?: string
      reviewerId?: string
      reviewerName?: string
      departmentId?: string
      departmentName?: string
      startAt?: string
      endAt?: string
      description?: string
      budget?: {
        id: string
        resultDescription: string
        items: Array<{
          id: string
          name: string
          targetFigure: number
          doneFigure: number
        }>
      }
      performance?: {
        id: string
        name: string
        unit: string
        items: Array<{
          id: string
          name: string
          targetFigure: number
          doneFigure: number
        }>
      }
      labels?: Array<{ id: string; key: string }>
      detailInfos?: Array<{ id: string; business_detail_field_id: string; key: string; name: string; value: string }>
      extraInfos?: Array<{ id: string; business_extra_field_id: string; key: string; name: string; value: string }>
    }
  } | null>(null)

  // Performance add selection modal state
  const [isPerformanceAddModalOpen, setIsPerformanceAddModalOpen] = useState(false)
  const [performanceAddParentData, setPerformanceAddParentData] = useState<{
    parentId: string
    parentName: string
    businessId: string
    businessName: string
  } | null>(null)

  // Performance create panel state
  const [isPerformanceCreatePanelOpen, setIsPerformanceCreatePanelOpen] = useState(false)

  // Task select modal state
  const [isTaskSelectModalOpen, setIsTaskSelectModalOpen] = useState(false)

  // Flag to prevent row click when action button is clicked
  const [isActionButtonClicked, setIsActionButtonClicked] = useState(false)

  // Fetch performance data
  const fetchPerformanceData = useCallback(async () => {
    if (!currentWorkspace?.id) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const response = await PerformanceService.appsPerformanceViewsGetWorkspacePerformanceAll({
        workspaceId: currentWorkspace.id
      })

      console.log('API 응답 원본 데이터:', response)

      // 연결세부지표 데이터 확인
      const checkTaskIdInResponse = (data: any, path = '') => {
        if (Array.isArray(data)) {
          data.forEach((item, index) => checkTaskIdInResponse(item, `${path}[${index}]`))
        } else if (data && typeof data === 'object') {
          if (data.link_type === 'task' || data.linkType === 'task') {
            console.log(`연결세부지표 발견 (${path}):`, {
              id: data.id,
              name: data.name,
              link_type: data.link_type,
              task_id: data.task_id
            })
          }
          Object.keys(data).forEach(key => {
            checkTaskIdInResponse(data[key], `${path}.${key}`)
          })
        }
      }
      checkTaskIdInResponse(response, 'response')

      const transformedData = transformPerformanceData(response)

      setPerformanceData(transformedData)

    } catch (err) {
      console.error('Failed to fetch performance data:', err)

      // Check if it's a network error (backend not running)
      if (err && (err.message?.includes('fetch') || err.message?.includes('Network') || err.message?.includes('Failed to fetch'))) {
        const errorMessage = ErrorMessages.NETWORK_ERROR
        setError(errorMessage)
        showError(errorMessage)
      } else {
        const errorMessage = ErrorMessages.FETCH_FAILED
        setError(errorMessage)
        handleApiError(err, showError, '성과 데이터를 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentWorkspace?.id, showError])

  // Fetch businesses using BusinessService API
  const fetchBusinesses = useCallback(async () => {
    if (!currentWorkspace?.id) {
      return
    }

    try {
      setIsBusinessesLoading(true)
      setBusinessesError(null)

      const response = await BusinessService.appsBusinessViewsListBusinesses({
        workspaceId: currentWorkspace.id,
        includeUnavailable: false
      })

      // Transform API response to BusinessSelect format
      const transformedBusinesses = (response.data || []).map(business => ({
        id: business.id,
        name: business.name
      }))

      setBusinesses(transformedBusinesses)

    } catch (err) {
      console.error('Failed to fetch businesses:', err)
      setBusinessesError('사업 목록을 불러오는데 실패했습니다.')
      handleApiError(err, showError, '사업 목록 조회 실패')
    } finally {
      setIsBusinessesLoading(false)
    }
  }, [currentWorkspace?.id, showError])

  // Initial data load - only on workspace change
  useEffect(() => {
    if (currentWorkspace?.id) {
      fetchPerformanceData()
    }
  }, [currentWorkspace?.id])

  // Fetch businesses on workspace change
  useEffect(() => {
    fetchBusinesses()
  }, [fetchBusinesses])

  // Helper function to check if any item in hierarchy matches search
  const matchesSearch = useCallback((searchTerm: string, performance: PerformanceTreeNode): boolean => {
    if (!searchTerm.trim()) return true
    
    const lowerSearchTerm = searchTerm.toLowerCase()
    
    // Check performance name
    if (performance.name.toLowerCase().includes(lowerSearchTerm)) {
      return true
    }
    
    // Check assignee name
    if (performance.assignee?.name.toLowerCase().includes(lowerSearchTerm)) {
      return true
    }
    
    // Check department name
    if (performance.department?.name.toLowerCase().includes(lowerSearchTerm)) {
      return true
    }
    
    // Check children recursively
    return performance.children.some(child => matchesSearch(searchTerm, child))
  }, [])
  
  // Deep filter function for department and member
  const filterByDepartmentAndMember = useCallback((nodes: PerformanceTreeNode[], departmentId: string, memberId: string): PerformanceTreeNode[] => {
    return nodes
      .map(node => {
        // If node has children, filter them recursively
        if (node.children && node.children.length > 0) {
          const filteredChildren = filterByDepartmentAndMember(node.children, departmentId, memberId)

          // For performance nodes, check if it matches the filter criteria
          if (node.type === 'performance') {
            const matchesDepartment = !departmentId || node.department?.id === departmentId
            const matchesMember = !memberId || node.assignee?.id === memberId

            if (matchesDepartment && matchesMember) {
              return { ...node, children: filteredChildren }
            }
            return null
          }

          // For non-performance nodes, keep them if they have matching children
          if (filteredChildren.length > 0) {
            return { ...node, children: filteredChildren }
          }
          return null
        }

        // Leaf node - check if it's a performance and matches criteria
        if (node.type === 'performance') {
          const matchesDepartment = !departmentId || node.department?.id === departmentId
          const matchesMember = !memberId || node.assignee?.id === memberId

          if (matchesDepartment && matchesMember) {
            return node
          }
          return null
        }

        // Non-performance leaf nodes without children - remove them
        return null
      })
      .filter((node): node is PerformanceTreeNode => node !== null)
  }, [])

  // Filter performance data based on business selection, department, member, and search
  const filteredPerformanceData = useMemo(() => {
    let filtered = performanceData

    // Filter by selected business
    if (selectedBusinessId) {
      filtered = filtered.filter(performance =>
        performance.businessId === selectedBusinessId || performance.id === selectedBusinessId
      )
    }

    // Filter by department and member
    if ((selectedDepartmentId && selectedDepartmentName !== '전체') || (selectedMemberId && selectedMemberName !== '전체')) {
      filtered = filterByDepartmentAndMember(
        filtered,
        selectedDepartmentName !== '전체' ? selectedDepartmentId : '',
        selectedMemberName !== '전체' ? selectedMemberId : ''
      )
    }

    // Filter by search value
    if (searchValue.trim()) {
      filtered = filtered.filter(performance =>
        matchesSearch(searchValue, performance)
      )
    }

    return filtered
  }, [performanceData, selectedBusinessId, selectedDepartmentId, selectedDepartmentName, selectedMemberId, selectedMemberName, searchValue, matchesSearch, filterByDepartmentAndMember])

  const handleBusinessSelect = useCallback((businessId: string, businessName: string) => {
    saveSelectedBusiness(businessId, businessName)
  }, [saveSelectedBusiness])

  const handleDepartmentSelect = useCallback((departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)
    // Reset member selection when department changes
    setSelectedMemberId('')
    setSelectedMemberName('')
  }, [])

  const handleMemberSelect = useCallback((memberId: string, memberName: string) => {
    setSelectedMemberId(memberId)
    setSelectedMemberName(memberName)
  }, [])

  const handleSearch = useCallback(() => {
    console.log('Searching for:', searchValue)
  }, [searchValue])

  const handleExpandToggle = useCallback(() => {
    setIsExpanded(prev => {
      const newExpanded = !prev
      // 테이블 ref를 통해 전체 확장/접기 실행
      if (newExpanded) {
        tableRef.current?.expandAll()
      } else {
        tableRef.current?.collapseAll()
      }
      
      return newExpanded
    })
  }, [])

  // const handleMoreOptions = useCallback(() => {
  //   console.warn('더보기 기능 미구현')
  // }, [])

  const handleCreatePerformanceIndicator = useCallback((businessId: string) => {
    // Set flag to prevent row click
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)
    
    // Find the business data to get the business name
    const business = performanceData.find(p => p.id === businessId)
    if (business) {
      setSelectedBusinessForIndicator({
        id: businessId,
        name: business.name
      })
      setIsIndicatorCreatePanelOpen(true)
    }
  }, [performanceData])

  const handleCreateCategory = useCallback((parentId: string) => {
    // Set flag to prevent row click
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)
    
    // Find parent performance and its associated group
    const findParentAndGroup = (items: PerformanceTreeNode[], targetId: string): {
      parent: PerformanceTreeNode | null,
      group: PerformanceTreeNode | null
    } => {
      // Recursive search function that tracks the group context
      const searchInNodes = (nodes: PerformanceTreeNode[], currentGroup: PerformanceTreeNode | null): PerformanceTreeNode | null => {
        for (const node of nodes) {
          if (node.id === targetId) {
            return node
          }
          
          // If this node is a group, update the current group context
          const nodeGroup = node.type === 'performance_group' ? node : currentGroup
          
          if (node.children && node.children.length > 0) {
            const found = searchInNodes(node.children, nodeGroup)
            if (found) return found
          }
        }
        return null
      }
      
      for (const businessItem of items) {
        if (businessItem.id === targetId) {
          // If the target is a business, we can't create categories under it
          return { parent: businessItem, group: null }
        }
        
        if (businessItem.children && businessItem.children.length > 0) {
          const found = searchInNodes(businessItem.children, null)
          if (found) {
            // Now find the group for this found node
            const findGroup = (nodes: PerformanceTreeNode[], target: PerformanceTreeNode): PerformanceTreeNode | null => {
              for (const node of nodes) {
                if (node.type === 'performance_group') {
                  // Check if target is this group or a descendant
                  if (node.id === target.id) {
                    return node
                  }
                  const isDescendant = (groupNode: PerformanceTreeNode, targetNode: PerformanceTreeNode): boolean => {
                    if (!groupNode.children) return false
                    for (const child of groupNode.children) {
                      if (child.id === targetNode.id) return true
                      if (isDescendant(child, targetNode)) return true
                    }
                    return false
                  }
                  if (isDescendant(node, target)) {
                    return node
                  }
                }
                if (node.children) {
                  const result = findGroup(node.children, target)
                  if (result) return result
                }
              }
              return null
            }
            
            const group = findGroup(businessItem.children, found)
            return { parent: found, group }
          }
        }
      }
      
      return { parent: null, group: null }
    }
    
    const { parent, group } = findParentAndGroup(performanceData, parentId)
    if (parent && group) {
      const categoryType = '분류'
      
      setCategoryCreateData({
        parentId: parentId,
        parentName: parent.name,
        groupId: group.id,
        groupName: group.name,
        categoryType: categoryType,
        isDirectUnderGroup: parent.type === 'performance_group'  // 부모가 지표구분인지 확인
      })
      setIsCategoryCreatePanelOpen(true)
    } else {
      console.error('Category creation failed: could not find parent or group', { parent: parent?.name, group: group?.name })
    }
  }, [performanceData])

  const handleCreatePerformanceClass = useCallback((categoryId: string) => {
    // Set flag to prevent row click
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)

    // Find the category data to get the category name
    const findCategory = (items: PerformanceTreeNode[], targetId: string): PerformanceTreeNode | null => {
      for (const item of items) {
        if (item.id === targetId && item.type === 'performance_category') {
          return item
        }
        if (item.children && item.children.length > 0) {
          const found = findCategory(item.children, targetId)
          if (found) return found
        }
      }
      return null
    }

    const category = findCategory(performanceData, categoryId)
    if (category) {
      setPerformanceClassCreateData({
        categoryId: categoryId,
        categoryName: category.name
      })
      setIsPerformanceClassCreatePanelOpen(true)
    }
  }, [performanceData])

  const handleEditPerformanceClass = useCallback((classId: string) => {
    console.log('성과지표 수정:', classId)

    // Set flag to prevent row click
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)

    // Find the performance class data and its parent category
    const findPerformanceClassWithParent = (items: PerformanceTreeNode[], targetId: string, parent?: PerformanceTreeNode): { performanceClass: PerformanceTreeNode; parentCategory?: PerformanceTreeNode } | null => {
      for (const item of items) {
        if (item.id === targetId && item.type === 'performance_class') {
          return { performanceClass: item, parentCategory: parent }
        }
        if (item.children && item.children.length > 0) {
          const found = findPerformanceClassWithParent(item.children, targetId, item.type === 'performance_category' ? item : parent)
          if (found) return found
        }
      }
      return null
    }

    const result = findPerformanceClassWithParent(performanceData, classId)
    if (result?.performanceClass) {
      setSelectedPerformanceClassForEdit({
        ...result.performanceClass,
        parentCategoryName: result.parentCategory?.name || ''
      } as PerformanceTreeNode & { parentCategoryName: string })
      setIsPerformanceClassEditPanelOpen(true)
    }
  }, [performanceData])

  const handleAddPerformance = useCallback((parentId: string) => {
    // Set flag to prevent row click
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)
    
    // Find parent and business context
    const findParentAndBusiness = (items: PerformanceTreeNode[], targetId: string): {
      parent: PerformanceTreeNode | null,
      business: PerformanceTreeNode | null
    } => {
      // First try to find in root level (businesses)
      for (const item of items) {
        if (item.id === targetId) {
          return { parent: item, business: item.type === 'business' ? item : null }
        }
      }
      
      // Then recursively search in children and track the business
      const searchInChildren = (nodes: PerformanceTreeNode[], business: PerformanceTreeNode): PerformanceTreeNode | null => {
        for (const node of nodes) {
          if (node.id === targetId) {
            return node
          }
          if (node.children && node.children.length > 0) {
            const found = searchInChildren(node.children, business)
            if (found) return found
          }
        }
        return null
      }
      
      for (const businessItem of items) {
        if (businessItem.type === 'business' && businessItem.children) {
          const found = searchInChildren(businessItem.children, businessItem)
          if (found) {
            return { parent: found, business: businessItem }
          }
        }
      }
      
      return { parent: null, business: null }
    }
    
    const { parent, business } = findParentAndBusiness(performanceData, parentId)
    if (parent && business) {
      setPerformanceAddParentData({
        parentId: parentId,
        parentName: parent.name,
        businessId: business.id,
        businessName: business.name
      })
      setIsPerformanceAddModalOpen(true)
    }
  }, [performanceData])

  const handleRowClick = useCallback((performance: PerformanceTreeNode) => {
    // Don't open detail panel if action button was just clicked
    if (isActionButtonClicked) {
      return
    }
    setSelectedPerformanceId(performance.id)
    setSelectedPerformanceType(performance.type)
    setIsDetailPanelOpen(true)
  }, [isActionButtonClicked])

  const handleDetailPanelClose = useCallback(() => {
    setIsDetailPanelOpen(false)
    setSelectedPerformanceId(null)
    setSelectedPerformanceType(null)
  }, [])

  const handlePerformanceEdit = useCallback((performance: PerformanceTreeNode) => {
    if (performance.type === 'performance_group') {
      // 지표구분 수정
      setSelectedGroupForEdit(performance)
      setIsGroupEditPanelOpen(true)
      setIsDetailPanelOpen(false)
    } else if (performance.type === 'performance_category') {
      // 하위분류 수정
      setSelectedCategoryForEdit(performance)
      setIsCategoryEditPanelOpen(true)
      setIsDetailPanelOpen(false)
    } else if (performance.type === 'performance_class') {
      // 성과지표 수정
      // Find parent category for the performance class
      const findParentCategory = (items: PerformanceTreeNode[], targetId: string, parent?: PerformanceTreeNode): PerformanceTreeNode | null => {
        for (const item of items) {
          if (item.id === targetId && item.type === 'performance_class') {
            return parent && parent.type === 'performance_category' ? parent : null
          }
          if (item.children && item.children.length > 0) {
            const found = findParentCategory(item.children, targetId, item)
            if (found) return found
          }
        }
        return null
      }

      const parentCategory = findParentCategory(performanceData, performance.id)
      setSelectedPerformanceClassForEdit({
        ...performance,
        parentCategoryName: parentCategory?.name || ''
      } as PerformanceTreeNode & { parentCategoryName: string })
      setIsPerformanceClassEditPanelOpen(true)
      setIsDetailPanelOpen(false)
    } else if (performance.type === 'performance') {
      // 세부지표 수정 - linkType에 따라 다른 패널 사용
      if (performance.linkType === 'direct') {
        // 직접 생성된 세부지표 - PerformanceEditPanel 사용
        setSelectedPerformanceForEdit(performance)
        setIsPerformanceEditPanelOpen(true)
        setIsDetailPanelOpen(false)
      } else if (performance.linkType === 'task') {
        // 태스크 연결 세부지표 - TaskCreatePanel 사용 (수정 모드)
        console.log('연결세부지표 데이터:', performance)
        console.log('task_id:', performance.task_id)

        // task_id가 없으면 에러 처리
        if (!performance.task_id) {
          console.error('연결세부지표에 task_id가 없습니다:', performance)
          showError('연결된 세부실행과제 정보를 찾을 수 없습니다.')
          setIsDetailPanelOpen(false)
          return
        }

        // orgHierarchy에서 사업명, 전략과제명, 실행과제명, 세부실행과제명 추출
        const hierarchy = performance.orgHierarchy || []
        const businessName = hierarchy[0] || ''
        const taskGroupName = hierarchy.length > 1 ? hierarchy[1] : ''
        const taskSubgroupName = hierarchy.length > 2 ? hierarchy[2] : ''

        // TaskService API를 호출하여 세부실행과제 데이터 가져오기
        const fetchTaskData = async () => {
          setIsLoadingTaskData(true)
          setIsDetailPanelOpen(false)

          try {
            const taskData = await TaskService.appsTaskViewsGetTask({
              taskId: performance.task_id!
            })

            // TaskFullResponse를 initialTaskData로 변환
            const initialTaskData: {
              name?: string
              assigneeId?: string
              assigneeName?: string
              subAssigneeId?: string
              subAssigneeName?: string
              managerId?: string
              managerName?: string
              reviewerId?: string
              reviewerName?: string
              departmentId?: string
              departmentName?: string
              startAt?: string
              endAt?: string
              description?: string
              budget?: {
                id: string
                resultDescription: string
                items: Array<{
                  id: string
                  name: string
                  targetFigure: number
                  doneFigure: number
                }>
              }
              performance?: {
                id: string
                name: string
                unit: string
                items: Array<{
                  id: string
                  name: string
                  targetFigure: number
                  doneFigure: number
                }>
              }
              labels?: Array<{ id: string; key: string }>
              detailInfos?: Array<{ id: string; business_detail_field_id: string; key: string; name: string; value: string }>
              extraInfos?: Array<{ id: string; business_extra_field_id: string; key: string; name: string; value: string }>
            } = {
              name: taskData.name,
              assigneeId: taskData.assignee.id,
              assigneeName: taskData.assignee.name,
              subAssigneeId: taskData.sub_assignee?.id,
              subAssigneeName: taskData.sub_assignee?.name,
              managerId: taskData.manager.id,
              managerName: taskData.manager.name,
              reviewerId: taskData.reviewer.id,
              reviewerName: taskData.reviewer.name,
              departmentId: taskData.department_id,
              departmentName: taskData.department_name,
              startAt: taskData.start_at || undefined,
              endAt: taskData.end_at || undefined,
              description: undefined, // TaskFullResponse에 description이 없음
              budget: taskData.budget ? {
                id: taskData.budget.id,
                resultDescription: taskData.budget.result_description || '',
                items: taskData.budget.items?.map(item => ({
                  id: item.id,
                  name: item.name,
                  targetFigure: parseFloat(item.target_figure) || 0,
                  doneFigure: parseFloat(item.done_figure) || 0
                })) || []
              } : undefined,
              performance: taskData.performance ? {
                id: taskData.performance.id,
                name: taskData.performance.name,
                unit: taskData.performance.unit || '',
                items: taskData.performance.items?.map(item => ({
                  id: item.id,
                  name: item.name,
                  targetFigure: parseFloat(item.target_figure || '0') || 0,
                  doneFigure: parseFloat(item.done_figure || '0') || 0
                })) || []
              } : undefined,
              labels: taskData.labels?.map(label => ({
                id: label.id || '',
                key: label.key
              })),
              detailInfos: taskData.detail_infos?.map(info => ({
                id: info.id || '',
                business_detail_field_id: info.business_detail_field_id || '',
                key: info.key,
                name: info.name,
                value: info.value
              })),
              extraInfos: taskData.extra_infos?.map(info => ({
                id: info.id || '',
                business_extra_field_id: info.business_extra_field_id || '',
                key: info.key,
                name: info.name,
                value: info.value
              }))
            }

            // 연결된 세부지표는 task를 가리킴
            setTaskEditData({
              taskId: performance.task_id!,
              taskSubgroupId: taskData.task_subgroup_id,
              taskSubgroupName: taskData.task_subgroup_name,
              taskGroupName: taskData.task_group_name || '',
              businessName: taskData.business_name || businessName,
              businessId: taskData.business_id || performance.business_id || performance.businessId || '',
              initialTaskData: initialTaskData
            })
            setIsTaskEditPanelOpen(true)
          } catch (err) {
            console.error('Failed to fetch task data:', err)
            showError('세부실행과제 데이터를 불러오는데 실패했습니다.')
          } finally {
            setIsLoadingTaskData(false)
          }
        }

        fetchTaskData()
      } else {
        // linkType이 없거나 알 수 없는 경우
        console.warn('알 수 없는 linkType:', performance.linkType)
        setIsDetailPanelOpen(false)
      }
    } else {
      // TODO: 다른 타입의 성과 수정 패널 열기
      console.warn('지원하지 않는 타입의 수정 요청:', performance.type)
      setIsDetailPanelOpen(false)
    }
  }, [performanceData])

  const handlePerformanceDelete = useCallback(async (performance: PerformanceTreeNode) => {
    try {
      if (performance.type === 'performance_group') {
        // 지표구분 삭제
        await PerformanceGroupService.appsPerformanceViewsDeletePerformanceGroup({
          groupId: performance.id
        })
      } else if (performance.type === 'performance_category') {
        // 하위분류 삭제
        await PerformanceCategoryService.appsPerformanceViewsDeletePerformanceCategory({
          categoryId: performance.id
        })
      } else {
        // TODO: 다른 타입의 성과 삭제 API 호출
        throw new Error(`${performance.type} 타입의 삭제 기능이 아직 구현되지 않았습니다.`)
      }
    } catch (err) {
      console.error('Failed to delete performance:', err)

      // Re-throw the error so it can be handled by the PerformanceDetailPanel
      if (err && typeof err === 'object' && 'status' in err) {
        throw err
      } else {
        throw new Error('삭제에 실패했습니다. 다시 시도해주세요.')
      }
    }
  }, [])

  const handleIndicatorCreatePanelClose = useCallback(() => {
    setIsIndicatorCreatePanelOpen(false)
    setSelectedBusinessForIndicator(null)
  }, [])

  const handleIndicatorCreateSuccess = useCallback(() => {
    // Refresh performance data after successful creation
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handleGroupEditPanelClose = useCallback(() => {
    setIsGroupEditPanelOpen(false)
    setSelectedGroupForEdit(null)
  }, [])

  const handleGroupEditSuccess = useCallback(() => {
    // Refresh performance data after successful update
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handleCategoryEditPanelClose = useCallback(() => {
    setIsCategoryEditPanelOpen(false)
    setSelectedCategoryForEdit(null)
  }, [])
  const handleCategoryEditSuccess = useCallback(() => {
    // Refresh performance data after successful update
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handleCategoryCreatePanelClose = useCallback(() => {
    setIsCategoryCreatePanelOpen(false)
    setCategoryCreateData(null)
  }, [])

  const handleCategoryCreateSuccess = useCallback(() => {
    // Refresh performance data after successful creation
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handlePerformanceClassCreatePanelClose = useCallback(() => {
    setIsPerformanceClassCreatePanelOpen(false)
    setPerformanceClassCreateData(null)
  }, [])

  const handlePerformanceClassCreateSuccess = useCallback(() => {
    // Refresh performance data after successful creation
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handlePerformanceClassEditPanelClose = useCallback(() => {
    setIsPerformanceClassEditPanelOpen(false)
    setSelectedPerformanceClassForEdit(null)
  }, [])

  const handlePerformanceClassEditSuccess = useCallback(() => {
    // Refresh performance data after successful update
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handlePerformanceEditPanelClose = useCallback(() => {
    setIsPerformanceEditPanelOpen(false)
    setSelectedPerformanceForEdit(null)
  }, [])

  const handlePerformanceEditSuccess = useCallback(() => {
    // Refresh performance data after successful update
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handleTaskEditPanelClose = useCallback(() => {
    setIsTaskEditPanelOpen(false)
    setTaskEditData(null)
  }, [])

  const handleTaskEditSuccess = useCallback(() => {
    // Refresh performance data after successful update
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handlePerformanceAddModalClose = useCallback(() => {
    setIsPerformanceAddModalOpen(false)
    setPerformanceAddParentData(null)
  }, [])

  const handleSelectLinkTask = useCallback(() => {
    setIsPerformanceAddModalOpen(false)
    setIsTaskSelectModalOpen(true)
  }, [])

  const handleSelectCreateNew = useCallback(() => {
    setIsPerformanceAddModalOpen(false)
    setIsPerformanceCreatePanelOpen(true)
  }, [])

  const handleSetActionButtonClicked = useCallback((clicked: boolean) => {
    setIsActionButtonClicked(clicked)
  }, [])

  const handlePerformanceCreatePanelClose = useCallback(() => {
    setIsPerformanceCreatePanelOpen(false)
  }, [])

  const handlePerformanceCreateSuccess = useCallback(() => {
    fetchPerformanceData()
  }, [fetchPerformanceData])

  const handleTaskSelectModalClose = useCallback(() => {
    setIsTaskSelectModalOpen(false)
  }, [])

  const handleTaskSelect = useCallback(async (task: { id: string; name: string }) => {
    if (!performanceAddParentData) {
      console.error('Parent data not available for task linking')
      return
    }

    try {
      const requestData: PerformanceMapCreateRequest = {
        performance_class_id: performanceAddParentData.parentId,
        link_type: 'task',
        target_id: task.id
      }

      await PerformanceMapService.appsPerformanceViewsCreatePerformanceMap({
        requestBody: requestData
      })

      // Close modal and refresh data
      setIsTaskSelectModalOpen(false)
      fetchPerformanceData()
      
    } catch (error) {
      console.error('Failed to link task:', error)
      handleApiError(error, showError, '업무 연결에 실패했습니다')
    }
  }, [performanceAddParentData, fetchPerformanceData, showError])

  const handleRefresh = useCallback(() => {
    fetchPerformanceData()
    tableRef.current?.refreshData()
  }, [fetchPerformanceData])

  // 드래그 앤 드롭 순서 변경 후 로컬 데이터 업데이트 핸들러
  const handlePerformanceDataUpdate = useCallback((newData: PerformanceTreeNode[]) => {
    setPerformanceData(newData)
  }, [])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{
          backgroundColor: designTokens.colors.gray[0]
        }}
      >
        {/* Business Selection */}
        <div className="w-[200px] flex-shrink-0">
          <BusinessSelect
            selectedBusinessId={selectedBusinessId}
            selectedBusinessName={selectedBusiness || '[전체] 사업 선택'}
            onSelect={handleBusinessSelect}
            businesses={businesses}
            showAllOption={true}
            placeholder="[전체] 사업 선택"
          />
        </div>

        {/* Department Selection */}
        <div className="w-[200px] flex-shrink-0">
          <DepartmentSelect
            workspaceId={currentWorkspace?.id || ''}
            selectedDepartmentId={selectedDepartmentId}
            selectedDepartmentName={selectedDepartmentName || '[전체] 부서 선택'}
            onSelect={handleDepartmentSelect}
            placeholder="[전체] 부서 선택"
            showAllOption={true}
          />
        </div>

        {/* Member Selection */}
        <div className="w-[200px] flex-shrink-0">
          <MemberSelect
            workspaceId={currentWorkspace?.id || ''}
            selectedMemberId={selectedMemberId}
            selectedMemberName={selectedMemberName || '[전체] 담당자 선택'}
            onSelect={handleMemberSelect}
            placeholder="[전체] 담당자 선택"
            label="담당자"
            showAllOption={true}
          />
        </div>

        {/* Search Input */}
        <div className="relative w-[280px] flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
            style={{
              backgroundColor: designTokens.colors.white,
              borderColor: designTokens.colors.gray[1]
            }}
          >
            <Search
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
            <input
              type="text"
              placeholder="내용을 입력해주세요."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 bg-transparent outline-none min-w-0"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                color: searchValue ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>

        {/* Search Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleSearch}
          className="h-10 px-[10px] flex-shrink-0 whitespace-nowrap"
        >
          검색
        </Button>

        {/* Expand Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleExpandToggle}
          className="h-10 px-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
        >
          <ChevronDown
            className={`w-[18px] h-[18px] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            style={{ color: designTokens.colors.gray[0] }}
          />
          {isExpanded ? '접기' : '펼침'}
        </Button>

        {/* More Options Button */}
        <div className="relative flex-shrink-0">
          <Button
            color="dark"
            size="medium"
            onClick={() => setIsMoreMenuOpen(prev => !prev)}
            className="h-10 px-[10px] flex items-center gap-1 whitespace-nowrap"
          >
            <MoreHorizontal
              className="w-[18px] h-[18px]"
              style={{ color: designTokens.colors.gray[0] }}
            />
            더보기
          </Button>

          {isMoreMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setIsMoreMenuOpen(false)} />
              <div
                className="absolute right-0 top-full mt-1 w-48 bg-white border rounded shadow-lg z-50"
                style={{ borderColor: designTokens.colors.gray[1] }}
              >
                <button
                  type="button"
                  className="w-full px-4 py-2.5 text-left transition-colors"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: hasAdminPrivileges ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    cursor: hasAdminPrivileges ? 'pointer' : 'default',
                  }}
                  disabled={!hasAdminPrivileges}
                  onClick={() => {
                    setIsMoreMenuOpen(false)
                    setIsBulkCreateModalOpen(true)
                  }}
                  onMouseEnter={(e) => { if (hasAdminPrivileges) e.currentTarget.style.backgroundColor = designTokens.colors.gray[0] }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  일괄 생성
                </button>
              </div>
            </>
          )}
        </div>
      </div>


      {/* Main Content Area */}
      <div className="flex-1 min-h-0 mt-6">
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          {/* Performance Hierarchy Table */}
          <PerformanceHierarchyTable
            ref={tableRef}
            data={filteredPerformanceData}
            loading={isLoading}
            error={error}
            onRowClick={handleRowClick}
            onCreatePerformanceIndicator={handleCreatePerformanceIndicator}
            onCreateCategory={handleCreateCategory}
            onCreatePerformanceClass={handleCreatePerformanceClass}
            onEditPerformanceClass={handleEditPerformanceClass}
            onAddPerformance={handleAddPerformance}
            onSetActionButtonClicked={handleSetActionButtonClicked}
            onDataUpdate={handlePerformanceDataUpdate}
          />
        </div>
      </div>

      {/* Performance Detail Panel — id/type만 전달, 패널이 자체 fetch */}
      <PerformanceDetailPanel
        isOpen={isDetailPanelOpen}
        onClose={handleDetailPanelClose}
        performanceId={selectedPerformanceId}
        performanceType={selectedPerformanceType}
        refreshKey={detailRefreshKey}
        onEdit={handlePerformanceEdit}
        onDelete={handlePerformanceDelete}
        onSuccess={() => {
          fetchPerformanceData()
          setDetailRefreshKey(k => k + 1)
        }}
      />

      {/* Performance Indicator Create Panel */}
      {selectedBusinessForIndicator && (
        <PerformanceIndicatorCreatePanel
          isOpen={isIndicatorCreatePanelOpen}
          onClose={handleIndicatorCreatePanelClose}
          workspaceId={currentWorkspace?.id || ''}
          businessId={selectedBusinessForIndicator.id}
          onSuccess={handleIndicatorCreateSuccess}
        />
      )}
      
      {/* Performance Group Edit Panel */}
      <PerformanceGroupEditPanel
        isOpen={isGroupEditPanelOpen}
        onClose={handleGroupEditPanelClose}
        performanceGroup={selectedGroupForEdit}
        onSuccess={handleGroupEditSuccess}
      />

      {/* Performance Category Edit Panel */}
      <PerformanceCategoryEditPanel
        isOpen={isCategoryEditPanelOpen}
        onClose={handleCategoryEditPanelClose}
        performanceCategory={selectedCategoryForEdit}
        onSuccess={handleCategoryEditSuccess}
      />

      {/* Category Create Panel */}
      {categoryCreateData && (
        <CategoryCreatePanel
          isOpen={isCategoryCreatePanelOpen}
          onClose={handleCategoryCreatePanelClose}
          parentId={categoryCreateData.parentId}
          parentName={categoryCreateData.parentName}
          groupId={categoryCreateData.groupId}
          groupName={categoryCreateData.groupName}
          categoryType={categoryCreateData.categoryType}
          isDirectUnderGroup={categoryCreateData.isDirectUnderGroup}
          onSuccess={handleCategoryCreateSuccess}
        />
      )}

      {/* Performance Class Create Panel */}
      {performanceClassCreateData && (
        <PerformanceClassCreatePanel
          isOpen={isPerformanceClassCreatePanelOpen}
          onClose={handlePerformanceClassCreatePanelClose}
          categoryId={performanceClassCreateData.categoryId}
          categoryName={performanceClassCreateData.categoryName}
          onSuccess={handlePerformanceClassCreateSuccess}
        />
      )}

      {/* Performance Class Edit Panel */}
      {selectedPerformanceClassForEdit && (
        <PerformanceClassEditPanel
          isOpen={isPerformanceClassEditPanelOpen}
          onClose={handlePerformanceClassEditPanelClose}
          classData={selectedPerformanceClassForEdit}
          categoryId={selectedPerformanceClassForEdit.performance_category_id || ''}
          categoryName={(selectedPerformanceClassForEdit as any).parentCategoryName || ''}
          onSuccess={handlePerformanceClassEditSuccess}
        />
      )}

      {/* Performance Add Selection Modal */}
      <PerformanceAddSelectionModal
        isOpen={isPerformanceAddModalOpen}
        onClose={handlePerformanceAddModalClose}
        onSelectLinkTask={handleSelectLinkTask}
        onSelectCreateNew={handleSelectCreateNew}
      />

      {/* Performance Create Panel */}
      {performanceAddParentData && (
        <PerformanceCreatePanel
          isOpen={isPerformanceCreatePanelOpen}
          onClose={handlePerformanceCreatePanelClose}
          parentClassId={performanceAddParentData.parentId}
          parentClassName={performanceAddParentData.parentName}
          onSuccess={handlePerformanceCreateSuccess}
        />
      )}

      {/* Task Select Modal */}
      <TaskSelectModal
        isOpen={isTaskSelectModalOpen}
        onClose={handleTaskSelectModalClose}
        onSelect={handleTaskSelect}
        parentCategoryName={performanceAddParentData?.parentName}
      />

      {/* Performance Edit Panel */}
      <PerformanceEditPanel
        isOpen={isPerformanceEditPanelOpen}
        onClose={handlePerformanceEditPanelClose}
        performanceData={selectedPerformanceForEdit}
        onSuccess={handlePerformanceEditSuccess}
      />

      {/* Task Edit Panel (for linked performances) */}
      {taskEditData && (
        <TaskCreatePanel
          isOpen={isTaskEditPanelOpen}
          onClose={handleTaskEditPanelClose}
          taskSubgroupId={taskEditData.taskSubgroupId}
          taskSubgroupName={taskEditData.taskSubgroupName}
          taskGroupName={taskEditData.taskGroupName}
          businessName={taskEditData.businessName}
          businessId={taskEditData.businessId}
          editMode={true}
          taskId={taskEditData.taskId}
          initialTaskData={taskEditData.initialTaskData}
          onSuccess={handleTaskEditSuccess}
        />
      )}

      {/* Bulk Create Modal */}
      <PerformanceBulkCreateModal
        isOpen={isBulkCreateModalOpen}
        onClose={() => setIsBulkCreateModalOpen(false)}
        onSuccess={fetchPerformanceData}
      />
    </div>
  )
}

export default PerformanceManagementPage